import { makeStyles } from "@mui/styles";
import { useTranslation } from "_metronic/i18n/language";
import { AnanseApiService } from "app/services";
import verifyCpfCnpj from "app/utils/validators/IsValidCpfCnpj";
import IsValidEmail from "app/utils/validators/IsValidEmail";
import IsValidName from "app/utils/validators/IsValidName";
import { ISelectSearchQuiz } from "interfaces";
import {
  ChangeEvent,
  FormEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { arrayToString } from "../utils";
import { userTypesData } from "../mock";

interface UserFormData {
  name: string;
  email: string;
  mobilePhone: string;
  tin: string;
  document: string;
  profile: string;
  birthday: string;
  genre: string;
  zipcode: string;
  street: string;
  city: string;
  region: string;
  country: string;
  nextPaymentDate: string;
  ativo: boolean; // Campo ativo para /useractive
  senha: string; // Campo senha de login para /useractive
  settingsPassword: string; // PIN para áreas restritas no app
  consumptionControl: string;
  userType: string;
  grouper: boolean;
  grouperId: number;
  usersLicense: number;
  devicesLicense: number;
  deletedAt: null;
  profiles: any[];
  disableProfile: boolean;
  showPassword: boolean;
  userId: number;
}

interface CreatedUser {
  id: number;
  nome: string;
  email: string;
  userType: string;
}

const useAdminUserCreateModel = () => {
  const translate = useTranslation();
  const history = useHistory();
  const api = new AnanseApiService();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [createdUser, setCreatedUser] = useState<CreatedUser | null>(null);
  const [quizzesSelected, setQuizzesSelected] = useState<ISelectSearchQuiz[]>(
    [],
  );
  const [loadingQuizUpdate, setLoadingQuizUpdate] = useState<boolean>(false);

  const [formData, setFormData] = useState<UserFormData>({
    name: "",
    email: "",
    mobilePhone: "",
    tin: "",
    document: "",
    profile: "",
    birthday: "",
    genre: "",
    zipcode: "",
    street: "",
    city: "",
    region: "",
    country: "BR",
    nextPaymentDate: "",
    ativo: true, // Permite marcar/desmarcar se o usuário está ativo
    senha: "", // Campo senha de login obrigatório
    settingsPassword: "", // PIN para áreas restritas
    consumptionControl: "",
    userType: "",
    grouper: false,
    grouperId: 0,
    usersLicense: 0,
    devicesLicense: 0,
    deletedAt: null,
    profiles: [],
    disableProfile: false,
    showPassword: false,
    userId: 0,
  });

  const steps = [
    translate("screens_user_create_step_form", "Novo Usuário"),
    translate("screens_user_create_step_quiz", "Selecionar e associar Testes"),
  ];

  const consumptionOptions = [
    {
      key: "",
      label: translate("screens_user_consumptionControl_optionsSelect"),
    },
    { key: "byTest", label: translate("screens_user_labels_byTest") },
    { key: "perCapsule", label: translate("screens_user_labels_perCapusle") },
    { key: "unlimited", label: translate("screens_user_labels_unlimited") },
  ];

  const currentCountry =
    formData.country === "BR"
      ? translate("screens_user_labels_cpf")
      : translate("screens_user_labels_document");

  useEffect(() => {
    loadProfiles();
    // Set page title
    if (window.setPageTitle) {
      window.setPageTitle(
        `${translate("screens_user_pageTitle_create")} - ${translate(
          "labels_admin",
        )}`,
      );
    }
  }, []);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      //   await EmularSegundoStepTestes();
      const profiles = await api.makeHttpRequest({
        url: "/perfil/",
      });

      const profilesData = profiles ? profiles.data : [];
      setFormData(prev => ({
        ...prev,
        profiles: profilesData,
        disableProfile: profilesData.length === 1,
        profile: profilesData.length === 1 ? profilesData[0].nome : "",
      }));
    } catch (error) {
      console.error("Error loading profiles:", error);
      Swal.fire(
        translate("defaultMessages_error"),
        translate("defaultMessages_loadData"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, nextPaymentDate: event.target.value }));
  };

  const handleChangeSenha = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData(prev => ({ ...prev, senha: event.target.value }));
  };

  const handleChangeSettingsPassword = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData(prev => ({ ...prev, settingsPassword: event.target.value }));
  };

  const handleToggleAtivo = () => {
    setFormData(prev => ({ ...prev, ativo: !prev.ativo }));
  };

  const handleSetGrouper = useCallback(
    (value: SetStateAction<boolean>) => {
      const newValue =
        typeof value === "function" ? value(formData.grouper) : value;
      setFormData(prev => ({ ...prev, grouper: newValue }));
    },
    [formData.grouper],
  );

  const handleSetUsersLicense = useCallback(
    (value: SetStateAction<number>) => {
      const newValue =
        typeof value === "function" ? value(formData.usersLicense) : value;
      setFormData(prev => ({ ...prev, usersLicense: newValue }));
    },
    [formData.usersLicense],
  );

  const handleSetDevicesLicense = useCallback(
    (value: SetStateAction<number>) => {
      const newValue =
        typeof value === "function" ? value(formData.devicesLicense) : value;
      setFormData(prev => ({ ...prev, devicesLicense: newValue }));
    },
    [formData.devicesLicense],
  );

  const handleSetGrouperId = useCallback(
    (value: SetStateAction<any>) => {
      const newValue =
        typeof value === "function" ? value(formData.grouperId) : value;
      setFormData(prev => ({ ...prev, grouperId: newValue }));
    },
    [formData.grouperId],
  );

  const toggleShowPassword = () => {
    setFormData(prev => ({ ...prev, showPassword: !prev.showPassword }));
  };

  const isResearchOrDemo = () => {
    const { userType } = formData;
    return ["demonstration", "researcher"].includes(userType);
  };

  const formIsValid = () => {
    const {
      name,
      email,
      senha,
      userType,
      consumptionControl,
      profile,
    } = formData;

    // Campos obrigatórios básicos
    if (
      !name ||
      !email ||
      !senha ||
      !userType ||
      !consumptionControl ||
      !profile
    ) {
      return false;
    }

    // Validações específicas
    if (!IsValidName(name) || !IsValidEmail(email)) {
      return false;
    }

    // Validação nextPaymentDate para tipos não demo/research
    if (!isResearchOrDemo() && !formData.nextPaymentDate) {
      return false;
    }

    // Validação CPF para Brasil
    if (
      formData.country === "BR" &&
      formData.tin &&
      !verifyCpfCnpj(formData.tin).isValid
    ) {
      return false;
    }

    return true;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // prettier-ignore
    try {
      const {
        name,
        tin,
        document,
        email,
        mobilePhone,
        profile,
        genre,
        birthday,
        zipcode,
        street,
        city,
        region,
        country,
        nextPaymentDate,
        settingsPassword,
        grouper,
        grouperId,
        usersLicense,
        devicesLicense,
        consumptionControl,
        userType,
      } = formData;

      const address =
        country !== ""
          ? {
              zipcode,
              street,
              city,
              state: region,
              country,
            }
          : null;

      const data = {
        email,
        mobilePhone,
        nome: name,
        cpf: tin !== "" ? tin : null,
        document: document !== "" ? document : null,
        ativo: formData.ativo, // Usar o campo ativo do formulário
        senha: formData.senha, // Usar o campo senha específico
        profile,
        birthday,
        genre,
        address,
        nextPaymentDate: isResearchOrDemo() ? null : nextPaymentDate,
        settingsPassword,
        grouper,
        grouperId: grouper ? 0 : grouperId,
        usersLicense: usersLicense ? usersLicense : 0,
        devicesLicense: devicesLicense ? devicesLicense : 0,
        consumptionControl,
        userType,
      };

      console.log("submit: ", data);

      const response = await api.makeHttpRequest({
        method: "POST",
        url: "/usuario/useractive", // Endpoint específico
        data,
      });

      setCreatedUser({
        id: response.id,
        nome: response.nome,
        email: response.email,
        userType: response.userType,
      });

      setActiveStep(1);
      Swal.fire(
        translate("defaultMessages_success"),
        translate("registration_updated_successfully"),
        "success",
      );
    } catch (error: any) {
      Swal.fire(
        translate("defaultMessages.error"),
        error.response && error.response.status === 400
          ? error.response.data
          : translate("screens.user.errors.register"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateQuizzes = async () => {
    try {
      if (quizzesSelected.length === 0 || !createdUser) {
        Swal.fire(
          translate("defaultMessages.error"),
          "Por favor selecione pelo menos um quiz primeiro",
          "error",
        );
        return;
      }

      setLoadingQuizUpdate(true);

      // Associar o usuário a todos os quizzes selecionados
      for (const quiz of quizzesSelected) {
        const currentUserIds = quiz.obj.userId || "";
        const userIdsArray = currentUserIds
          ? currentUserIds.split(";").map(Number)
          : [];
        const newUserIds = [...userIdsArray, createdUser.id];
        const updatedUserIds = arrayToString(newUserIds);

        await api.makeHttpRequest({
          method: "PUT",
          url: `/quiz/${quiz.id}`,
          data: { ...quiz.obj, userId: updatedUserIds },
        });
      }

      Swal.fire(
        translate("defaultMessages_success"),
        `Usuário associado a ${quizzesSelected.length} quiz(zes) com sucesso!`,
        "success",
      );

      setQuizzesSelected([]);
    } catch (error) {
      console.error("Error updating quizzes:", error);
      Swal.fire(
        translate("defaultMessages_error"),
        translate("defaultMessages_errorChangeStatus"),
        "error",
      );
    } finally {
      setLoadingQuizUpdate(false);
    }
  };

  const onCleanForm = () => {
    setFormData({
      name: "",
      email: "",
      mobilePhone: "",
      tin: "",
      document: "",
      profile: "",
      birthday: "",
      genre: "",
      zipcode: "",
      street: "",
      city: "",
      region: "",
      country: "BR",
      nextPaymentDate: "",
      ativo: false, // Permite marcar/desmarcar se o usuário está ativo
      senha: "", // Campo senha de login
      settingsPassword: "", // PIN para áreas restritas
      consumptionControl: "",
      userType: "",
      grouper: false,
      grouperId: 0,
      usersLicense: 0,
      devicesLicense: 0,
      deletedAt: null,
      profiles: formData.profiles, // Manter profiles carregados
      disableProfile: formData.disableProfile,
      showPassword: false,
      userId: 0,
    });
  };

  const handleFinish = () => {
    Swal.fire({
      title: translate(
        "screens_user_create_complete_title",
        "Usuário criado e configurado!",
      ),
      text: translate(
        "screens_user_create_complete_text",
        "Este usuário ja pode acessar o sistema.",
      ),
      icon: "success",
      showCancelButton: true,
      confirmButtonText: translate(
        "screens_user_create_complete_new",
        "Criar novo usuário",
      ),
      cancelButtonText: translate(
        "screens_user_create_complete_list",
        "Listar usuários",
      ),
    }).then(result => {
      if (result.isConfirmed) {
        // Resetar formulário para criar novo usuário
        onCleanForm();
        setCreatedUser(null);
        setQuizzesSelected([]);
        setActiveStep(0);
      } else {
        history.push("/admin/userlist");
      }
    });
  };

  const EmularSegundoStepTestes = async () => {
    const response = await api.makeHttpRequest({
      method: "GET",
      url: `/usuario/${238}`,
    });

    setCreatedUser({
      id: response.id,
      nome: response.nome,
      email: response.email,
      userType: response.userType,
    });

    setActiveStep(1);
    Swal.fire(
      translate("defaultMessages_success"),
      translate("registration_updated_successfully"),
      "success",
    );
  };

  return {
    translate,
    activeStep,
    steps,
    loadingQuizUpdate,
    formData,
    consumptionOptions,
    loading,
    createdUser,
    quizzesSelected,
    currentCountry,
    userTypesData,
    setActiveStep,
    onCleanForm,
    isResearchOrDemo,
    IsValidName,
    IsValidEmail,
    verifyCpfCnpj,
    handleChange,
    handleChangeDate,
    handleChangeSenha,
    handleChangeSettingsPassword,
    handleToggleAtivo,
    handleSetGrouper,
    handleSetUsersLicense,
    handleSetDevicesLicense,
    handleSetGrouperId,
    toggleShowPassword,
    formIsValid,
    submit,
    setQuizzesSelected,
    updateQuizzes,
    handleFinish,
  };
};

export default useAdminUserCreateModel;
